import "./styles.scss";

import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";
import { useNewWebsite } from "context/client/new_website";

import DetailsCard from "components/DetailsCard";
import FormikInput from "components/formik/FormikInput";
import FormikSelect from "components/formik/FormikSelect";
import FormikTaxInput from "components/formik/FormikTaxInput";
import FormikPhoneInput from "components/formik/FormikPhoneInput";
import Widget from "../../Widget";
import NavMobile from "../../NavMobile";

import useResponsive from "libs/helpers/useResponsive";
import countries from "libs/countries.json";
import yourDataSchema, {
  yourDataSchemaWithoutNipCheck,
} from "libs/validations/new_website_form/yourDataSchema";

const mainClass = "new-website-content__your-data";

const YourData = () => {
  const { t, i18n } = useTranslation();
  const { min } = useResponsive();

  const { websites } = useApp();

  const {
    website,
    form_data,
    change_plan_mode,
    nextStep,
    prevStep,
    form_data_error,
  } = useNewWebsite();

  const onSubmit = (values) => {
    nextStep(values);
  };

  const select_options = countries?.map(({ name, name_pl, code }) => ({
    label: i18n?.language === "pl" ? name_pl || name : name,
    value: code,
  }));

  const first_website = websites?.[0];

  return (
    <Formik
      initialValues={{
        first_name: !!change_plan_mode
          ? website?.contact_data?.first_name
          : form_data?.first_name ||
            first_website?.contact_data?.first_name ||
            "",
        last_name: !!change_plan_mode
          ? website?.contact_data?.last_name
          : form_data?.last_name ||
            first_website?.contact_data?.last_name ||
            "",
        email: !!change_plan_mode
          ? website?.contact_data?.email
          : form_data?.email || first_website?.contact_data?.email || "",
        phone: {
          code: !!change_plan_mode
            ? website?.contact_data?.phone?.code
            : form_data?.phone?.code ||
              first_website?.contact_data?.phone?.code ||
              "",
          number: !!change_plan_mode
            ? website?.contact_data?.phone?.number
            : form_data?.phone?.number ||
              first_website?.contact_data?.phone?.number ||
              "",
        },
        billing: {
          name: !!change_plan_mode
            ? website?.billing_data?.name
            : form_data?.billing?.name ||
              first_website?.billing_data?.name ||
              "",
          tax: {
            code: !!change_plan_mode
              ? website?.billing_data?.tax?.code
              : form_data?.billing?.tax?.code ||
                first_website?.billing_data?.tax?.code ||
                "",
            number: !!change_plan_mode
              ? website?.billing_data?.tax?.clean_number
              : form_data?.billing?.tax?.number ||
                first_website?.billing_data?.tax?.clean_number ||
                "",
          },
          street: !!change_plan_mode
            ? website?.billing_data?.street
            : form_data?.billing?.street ||
              first_website?.billing_data?.street ||
              "",
          city: !!change_plan_mode
            ? website?.billing_data?.city
            : form_data?.billing?.city ||
              first_website?.billing_data?.city ||
              "",
          post_code: !!change_plan_mode
            ? website?.billing_data?.post_code
            : form_data?.billing?.post_code ||
              first_website?.billing_data?.post_code ||
              "",
          country: !!change_plan_mode
            ? website?.billing_data?.country
            : form_data?.billing?.country ||
              first_website?.billing_data?.country ||
              "",
        },
      }}
      validationSchema={
        !!change_plan_mode ? yourDataSchemaWithoutNipCheck : yourDataSchema
      }
      validateOnMount
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values, setSubmitting);
      }}
    >
      {({ values, isSubmitting, isValid }) => (
        <Form className={mainClass} autoComplete="off">
          <DetailsCard
            {...(min.lg
              ? { title: t("Back"), backAction: () => prevStep() }
              : {})}
            resetOnMobile
            no_overflow
          >
            <div className={`${mainClass}__row`}>
              <div className={`${mainClass}__column`}>
                <DetailsCard
                  title={t("Contact data")}
                  largeHeaderMobileText
                  no_overflow
                >
                  <div className={`${mainClass}__form form`}>
                    <FormikInput
                      name="first_name"
                      label={t("Name")}
                      placeholder={t("John")}
                      disabled={isSubmitting || !!change_plan_mode}
                      required
                    />
                    <FormikInput
                      name="last_name"
                      label={t("Surname")}
                      placeholder={t("Doe")}
                      disabled={isSubmitting || !!change_plan_mode}
                      required
                    />
                    <FormikInput
                      name="email"
                      type="email"
                      label={t("Email")}
                      placeholder={t("j.doe@domain.com")}
                      disabled={isSubmitting || !!change_plan_mode}
                      required
                    />
                    <FormikPhoneInput
                      label={t("Phone")}
                      name="phone"
                      placeholder="123 456 789"
                      required
                      disabled={isSubmitting || !!change_plan_mode}
                    />
                  </div>
                </DetailsCard>
                <DetailsCard
                  title={t("Invoice details")}
                  largeHeaderMobileText
                  no_overflow
                >
                  <div className={`${mainClass}__form form`}>
                    <FormikInput
                      name="billing.name"
                      label={t("Company name")}
                      disabled={isSubmitting || !!change_plan_mode}
                      placeholder={t("Acme Corporation")}
                      required
                    />
                    <FormikTaxInput
                      label={t("Tax identification number")}
                      name="billing.tax"
                      disabled={isSubmitting || !!change_plan_mode}
                      invalid_tax={
                        form_data_error === "tax_id_invalid" ? true : false
                      }
                      required
                    />
                    <FormikInput
                      name="billing.street"
                      label={t("Street")}
                      disabled={isSubmitting || !!change_plan_mode}
                      placeholder={t("Main Street 13A")}
                      required
                    />
                    <FormikInput
                      name="billing.city"
                      label={t("City")}
                      placeholder={t("London")}
                      disabled={isSubmitting || !!change_plan_mode}
                      required
                    />
                    <div className={`${mainClass}__form-columns`}>
                      <FormikInput
                        name="billing.post_code"
                        label={t("Post code")}
                        disabled={isSubmitting || !!change_plan_mode}
                        placeholder="30-123"
                        required
                      />
                      <FormikSelect
                        name="billing.country"
                        label={t("Country")}
                        options={select_options}
                        selected={values?.billing?.country}
                        disabled={isSubmitting || !!change_plan_mode}
                        searcher={true}
                        placeholder={t("United Kingdom")}
                        required
                      />
                    </div>
                  </div>
                </DetailsCard>
              </div>
              {min.lg && (
                <div className={`${mainClass}__column`}>
                  <Widget isNextDisabled={!isValid} />
                </div>
              )}
            </div>
            <p className={`${mainClass}__info`}>
              {t(
                "You will be able to change the above information in the client panel after adding the site to the system.",
              )}
            </p>
            {!min.lg && (
              <NavMobile isNextDisabled={!isValid} onBack={() => prevStep()} />
            )}
          </DetailsCard>
        </Form>
      )}
    </Formik>
  );
};

export default YourData;
